import { Baby, CalendarClock, Sparkles, TrendingUp } from "lucide-react";
import { Reveal, scrollToId } from "@/components/Reveal";

const STEPS = [
  {
    icon: Baby,
    title: "Start at birth — or today",
    body: "Time is the multiplier. Every year you wait costs more than any contribution ever will.",
  },
  {
    icon: CalendarClock,
    title: "Automate a monthly gift",
    body: "Even a modest monthly amount, set on autopilot, quietly becomes a fortune over two decades.",
  },
  {
    icon: TrendingUp,
    title: "Let compounding build the legacy",
    body: "Tax-advantaged growth vehicles turn steady deposits into a launchpad for college, a first home, or a first business.",
  },
];

const MILESTONES = [
  { age: "18", amount: "$149K", label: "College, fully funded" },
  { age: "30", amount: "$448K", label: "A home, a business, a head start" },
  { age: "40", amount: "$1.05M", label: "A million-dollar legacy" },
];

export const BabyFund = () => (
  <section data-testid="baby-fund-section" className="relative overflow-hidden bg-[#0F091A] py-28 lg:py-40">
    <div className="absolute -left-32 top-1/4 h-[26rem] w-[26rem] rounded-full bg-[#2E1065]/40 blur-[140px]" aria-hidden="true" />
    <div className="absolute -right-32 bottom-0 h-[22rem] w-[22rem] rounded-full bg-gold/8 blur-[150px]" aria-hidden="true" />

    <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <p className="eyebrow mb-6" data-testid="baby-fund-eyebrow">Spotlight</p>
        <h2 className="max-w-3xl font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl lg:text-4xl">
          The Million Dollar
          <span className="text-gold-gradient"> Baby Fund.</span>
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/55" data-testid="baby-fund-intro">
          A small, steady gift started in childhood can become a seven-figure legacy. Here is how
          families begin — and what consistency can grow into.
        </p>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20">
        <div className="space-y-4">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="gold-border-glow group flex items-start gap-6 bg-wine/70 p-7" data-testid={`baby-fund-step-${i + 1}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold/30 bg-royal/60 text-gold transition-colors duration-500 group-hover:bg-gold group-hover:text-obsidian">
                    <Icon size={18} strokeWidth={1.5} />
                  </span>
                  <span>
                    <span className="flex items-baseline gap-3">
                      <span className="font-mono text-xs text-gold/50">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-display text-lg font-medium text-white">{s.title}</span>
                    </span>
                    <span className="mt-2 block text-sm leading-relaxed text-white/50">{s.body}</span>
                  </span>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.15} className="flex items-center">
          <div className="gold-border-glow w-full bg-wine/80 p-10" data-testid="baby-fund-milestones-card">
            <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold/80">
              <Sparkles size={13} /> $300/month from birth · illustrative
            </p>
            <div className="mt-8 space-y-0">
              {MILESTONES.map((m, i) => (
                <div
                  key={m.age}
                  className="flex items-baseline justify-between border-t border-gold/10 py-6 first:border-t-0 first:pt-0"
                  data-testid={`baby-fund-milestone-${m.age}`}
                >
                  <span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">Age {m.age}</span>
                    <span className="mt-1 block text-xs text-white/45">{m.label}</span>
                  </span>
                  <span className={`font-display font-semibold ${i === MILESTONES.length - 1 ? "text-3xl text-gold-gradient" : "text-2xl text-white/80"}`}>
                    {m.amount}
                  </span>
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent("va:preset-service", { detail: "Million Dollar Baby Fund" }));
                scrollToId("#contact");
              }}
              data-testid="baby-fund-cta"
              className="mt-8 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-obsidian transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_40px_rgba(212,175,55,0.35)]"
            >
              Start a Baby Fund
            </button>
            <p className="mt-6 text-xs leading-relaxed text-white/35" data-testid="baby-fund-disclaimer">
              Illustrative figures assume 8% annual growth and are not a guarantee or financial advice.
              Actual results vary by product, market performance, and contribution schedule.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
